import { useState, useEffect, useRef } from 'react';
import { Play, Pause, HelpCircle, X } from 'lucide-react';
import { formatTime } from '../../utils';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface TimerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  duration: number;
  compact?: boolean;
  showHalftime?: boolean;
  showQuiz?: boolean;
  onHalftime?: () => void;
  onFifteenSeconds?: () => void;
  onTime?: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onOpenQuiz?: () => void;
}

function progressColor(timeLeft: number, duration: number): string {
  const halftime = Math.floor(duration / 2);
  if (timeLeft <= 15) return '#ef4444';    // red — fifteen seconds
  if (timeLeft <= halftime) return '#f97316'; // orange — half-time
  return '#22c55e';                          // green — default
}

export function Timer({
  isOpen,
  onClose,
  title,
  duration,
  compact = false,
  showHalftime = false,
  showQuiz = false,
  onHalftime,
  onFifteenSeconds,
  onTime,
  onPause,
  onResume,
  onOpenQuiz
}: TimerProps) {
  const { t } = useTranslation();

  const [timeLeft, setTimeLeft] = useState(duration);
  const [isRunning, setIsRunning] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedElapsedRef = useRef<number>(0);

  const announcementsTriggered = useRef({
    halftime: false,
    fifteenSeconds: false,
    time: false
  });

  useEffect(() => {
    if (isOpen) {
      announcementsTriggered.current = { halftime: false, fifteenSeconds: false, time: false };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const halftimeValue = Math.floor(duration / 2);
    if (showHalftime && timeLeft === halftimeValue && !announcementsTriggered.current.halftime) {
      announcementsTriggered.current.halftime = true;
      onHalftime?.();
    }
    if (timeLeft === 15 && !announcementsTriggered.current.fifteenSeconds) {
      announcementsTriggered.current.fifteenSeconds = true;
      onFifteenSeconds?.();
    }
  }, [timeLeft, duration, showHalftime, onHalftime, onFifteenSeconds, isOpen]);

  const onTimeRef = useRef(onTime);
  onTimeRef.current = onTime;

  useEffect(() => {
    if (isRunning && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        const elapsedMs = pausedElapsedRef.current + (Date.now() - startTimeRef.current);
        const remaining = Math.max(duration - Math.floor(elapsedMs / 1000), 0);
        setTimeLeft(remaining);
        if (remaining <= 0) {
          setIsRunning(false);
          if (!announcementsTriggered.current.time) {
            announcementsTriggered.current.time = true;
            onTimeRef.current?.();
          }
        }
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isRunning, timeLeft, duration]);

  useEffect(() => {
    if (isOpen) {
      setTimeLeft(duration);
      setIsRunning(true);
      startTimeRef.current = Date.now();
      pausedElapsedRef.current = 0;
    }
  }, [isOpen, duration]);

  if (!isOpen) return null;

  const ratio = duration > 0 ? timeLeft / duration : 0;
  const color = progressColor(timeLeft, duration);

  if (compact) {
    return (
      <div className="fixed top-12 left-0 right-0 z-50 bg-gray-800">
        {/* Progress bar */}
        <div className="h-2 bg-gray-600 w-full">
          <div
            className="h-full transition-all duration-1000 ease-linear"
            style={{ width: `${ratio * 100}%`, backgroundColor: color }}
          />
        </div>
        {/* Compact controls */}
        <div className="flex items-center justify-between px-3 py-2">
          <div className="flex items-center gap-3">
            <span className="text-white font-mono font-bold text-lg" style={{ color }}>
              {formatTime(timeLeft)}
            </span>
            <span className="text-gray-400 text-xs">{title}</span>
          </div>
          <div className="flex items-center gap-2">
            {timeLeft > 0 && (
              !isRunning ? (
                <button
                  onClick={() => { startTimeRef.current = Date.now(); setIsRunning(true); onResume?.(); }}
                  className="text-green-400 p-1"
                >
                  <Play size={16} />
                </button>
              ) : (
                <button
                  onClick={() => { pausedElapsedRef.current += Date.now() - startTimeRef.current; setIsRunning(false); onPause?.(); }}
                  className="text-gray-400 p-1"
                >
                  <Pause size={16} />
                </button>
              )
            )}
            <button onClick={onClose} className="text-gray-400 p-1">
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white max-w-sm w-full mx-4 overflow-hidden">

        {/* Progress bar */}
        <div className="h-4 bg-gray-100 w-full">
          <div
            className="h-full transition-all duration-1000 ease-linear"
            style={{ width: `${ratio * 100}%`, backgroundColor: color }}
          />
        </div>

        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">{title}</h3>

          {/* Time display */}
          <div className="text-center mb-6">
            <div className="text-5xl font-mono font-bold" style={{ color }}>
              {formatTime(timeLeft)}
            </div>
          </div>

          {/* Buttons */}
          {timeLeft === 0 ? (
            <button onClick={onClose} className="w-full bg-gray-500 text-white py-2 px-4">
              {t('common.close')}
            </button>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                {!isRunning ? (
                  <button
                    onClick={() => { startTimeRef.current = Date.now(); setIsRunning(true); onResume?.(); }}
                    className="bg-green-500 text-white py-2 px-4 flex items-center justify-center gap-2"
                  >
                    <Play size={16} />
                    {t('common.resume')}
                  </button>
                ) : (
                  <button
                    onClick={() => { pausedElapsedRef.current += Date.now() - startTimeRef.current; setIsRunning(false); onPause?.(); }}
                    className="bg-gray-400 text-white py-2 px-4 flex items-center justify-center gap-2"
                  >
                    <Pause size={16} />
                    {t('common.pause')}
                  </button>
                )}
                <button onClick={onClose} className="bg-gray-500 text-white py-2 px-4">
                  {t('common.close')}
                </button>
              </div>

              {/* Quiz link */}
              {showQuiz && onOpenQuiz && (
                <button
                  onClick={onOpenQuiz}
                  className="flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 text-sm py-2 transition-colors"
                >
                  <HelpCircle size={16} />
                  {t('quiz.title')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
